import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue'
  import type { PropType } from 'vue'
  import useCommonMixin from '@/composables/useCommonMixin'
  import { UserModule } from '@/store/modules/user'
  import useIcons from '@/composables/useIcons'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import OverviewActionBar from '@/components/user-management/OverviewActionBar.vue'
  import UserTable from '@/components/user-management/UserTable.vue'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import UsersAndGroupsLoader from '@/components/loaders/UsersAndGroupsLoader.vue'
  import eventBus from '@/main'
  import useI18n from '@/composables/useI18n'
  import type { UserMember, UserMembersResponse } from '@/services/interfaces/Auth0'
  import UsersApi from '@/services/api/UsersApi'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'UserManagementTabView',
  props: {
    openInviteUserModal: {
      required: true,
      type: Function as PropType<() => void>,
    },
    reloadUsers: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['create-new-group', 'reloaded-users'],
  setup(__props, { emit }) {

const props = __props;

  

  

  const { closeIcon, searchIcon, plusVectorIcon, download } = useIcons()
  const { userMembersPerPageLimit } = useCommonMixin()
  const { translateString } = useI18n()

  const userMembers = ref(null as UserMembersResponse | null)
  const selectedUser = ref({} as { [key: string]: UserMember | undefined })
  const searchTerm = ref('')
  const loadingMoreUsers = ref(false)
  const activeSearch = ref(false)
  const searchingUsers = ref(false)
  const showSearchTooltip = ref(false)
  const exportingUsers = ref(false)

  const allUserRolesMembers = computed(() => {
    if (activeSearch.value) return UserModule.searchedUserMembers
    return userMembers.value?.roles
  })

  const specificRoleMembersResponse = computed(() => (role: string) => {
    return allUserRolesMembers.value?.find((r) => r.roleName === role)
  })

  const specificRoleUsers = computed(() => (role: string) => {
    return specificRoleMembersResponse.value(role)?.users ?? []
  })

  const overallTotalUsers = computed(() => userMembers.value?.totalUsers ?? 0)

  const specificRoleUsersCount = computed(() => (role: string) => {
    if (!specificRoleMembersResponse.value(role)) return 0
    return specificRoleMembersResponse.value(role)?.users.length ?? 0
  })

  const specificRoleTotalUsersCount = computed(() => (role: string) => {
    if (!specificRoleMembersResponse.value(role)) return 0
    return specificRoleMembersResponse.value(role)?.totalUsers ?? 0
  })

  const xMoreUsersToLoad = computed(() => (role: string) => {
    return specificRoleTotalUsersCount.value(role) - specificRoleUsersCount.value(role) > userMembersPerPageLimit.value
      ? userMembersPerPageLimit.value
      : specificRoleTotalUsersCount.value(role) - specificRoleUsersCount.value(role)
  })

  const loadedAllRoleUsers = computed(() => (role: string) => {
    return specificRoleTotalUsersCount.value(role) === specificRoleUsersCount.value(role)
  })

  const loadXMore = computed(
    () => (role: string) => translateString('loadXMore', { number: xMoreUsersToLoad.value(role) }),
  )

  const userMemberRolesToGroup = computed(() => {
    return ['COMPANY_ADMIN', 'EDITOR', 'USER']
  })

  const customUserRoleText = computed(() => {
    return (role: string) => {
      if (role === 'COMPANY_ADMIN') {
        return translateString('homepage.administrators')
      }
      if (role === 'EDITOR') {
        return translateString('homepage.editors')
      }
      return translateString('homepage.user')
    }
  })

  const userRoleDescription = computed(() => {
    return (role: string) => {
      if (role === 'COMPANY_ADMIN') {
        return translateString('homepage.administratorsRoleDescription')
      }
      if (role === 'EDITOR') {
        return translateString('homepage.editorRoleDescription')
      }
      return translateString('homepage.userRoleDescription')
    }
  })

  const roleName = computed(() => (roleId: string) => UserModule.roles.find((r) => r.roleId === roleId)?.name)

  const exportUsersAsCSV = () => {
    exportingUsers.value = true
    UsersApi.exportCompanyUsersAsCSV(UserModule.currentCompany!.id)
      .then(() => {
        eventBus.$toasted.success(translateString('exportUsersSuccessful') as string)
      })
      .finally(() => (exportingUsers.value = false))
  }

  const setSelectedUser = (user: UserMember, role: string) => {
    // first reset selectedUser
    selectedUser.value = {}
    eventBus.$set(selectedUser.value, role, user)
  }

  const searchUsers = () => {
    if (searchTerm.value.length < 3) {
      showSearchTooltip.value = true
    } else {
      showSearchTooltip.value = false
      activeSearch.value = true
      searchingUsers.value = true
      UserModule.getSearchedUserMembers({
        perPage: userMembersPerPageLimit.value,
        page: 1,
        term: searchTerm.value,
        roles: ['COMPANY_ADMIN', 'EDITOR', 'USER'],
      }).then(() => {
        searchingUsers.value = false
      })
    }
  }

  const clearSearchTerm = () => {
    if (searchTerm.value) {
      activeSearch.value = false
      searchingUsers.value = false
      searchTerm.value = ''
    }
  }

  const deleteUser = (deleteUserData: { userId: string; rolesUserHas: string[] }) => {
    const { userId, rolesUserHas } = deleteUserData
    if (!rolesUserHas.length) {
      // if the user has no role that means they have the default USER role
      spliceOutDeletedUser('USER', userId)
    } else {
      rolesUserHas.forEach((roleId) => {
        const role = roleName.value(roleId)
        if (role) {
          spliceOutDeletedUser(role, userId)
        }
      })
    }
    clearSearchTerm()
  }

  const spliceOutDeletedUser = (roleName: string, userId: string) => {
    if (specificRoleUsers.value(roleName)) {
      const roleIndex = userMembers.value?.roles.findIndex((r) => r.roleName === roleName)
      const userToDeleteIndex = specificRoleUsers.value(roleName).findIndex((user) => user.id === userId)
      if (typeof roleIndex === 'number' && typeof userToDeleteIndex === 'number') {
        userMembers.value?.roles[roleIndex].users.splice(userToDeleteIndex, 1)
      }
    }
  }

  const loadMoreUsers = (role: string) => {
    if (specificRoleMembersResponse.value(role)) {
      loadingMoreUsers.value = true
      const payload: any = {
        perPage: userMembersPerPageLimit.value,
        page: specificRoleMembersResponse.value(role)!.currentPage + 1,
        roles: [role],
        userCategory: activeSearch.value ? 'search' : 'roles',
      }
      if (activeSearch.value) {
        payload.term = searchTerm.value
      }
      UserModule.loadMoreUsers(payload).finally(() => (loadingMoreUsers.value = false))
    }
  }

  const loadAllRoleUsers = (role: string) => {
    if (specificRoleMembersResponse.value(role)) {
      loadingMoreUsers.value = true
      const payload: any = {
        perPage: userMembersPerPageLimit.value,
        page: specificRoleMembersResponse.value(role)!.currentPage + 1,
        roles: [role],
        userCategory: activeSearch.value ? 'search' : 'roles',
        loadAll: true,
      }
      if (activeSearch.value) {
        payload.search = searchTerm.value
      }
      UserModule.loadMoreUsers(payload).finally(() => (loadingMoreUsers.value = false))
    }
  }

  const reloadUserMembers = () => {
    userMembers.value = UserModule.userMembers
  }

  watch(
    () => props.reloadUsers,
    (value) => {
      if (value) {
        reloadUserMembers()
        emit('reloaded-users')
      }
    },
  )

  userMembers.value = UserModule.userMembers

return { __sfc: true,props, emit, closeIcon, searchIcon, plusVectorIcon, download, userMembersPerPageLimit, translateString, userMembers, selectedUser, searchTerm, loadingMoreUsers, activeSearch, searchingUsers, showSearchTooltip, exportingUsers, allUserRolesMembers, specificRoleMembersResponse, specificRoleUsers, overallTotalUsers, specificRoleUsersCount, specificRoleTotalUsersCount, xMoreUsersToLoad, loadedAllRoleUsers, loadXMore, userMemberRolesToGroup, customUserRoleText, userRoleDescription, roleName, exportUsersAsCSV, setSelectedUser, searchUsers, clearSearchTerm, deleteUser, spliceOutDeletedUser, loadMoreUsers, loadAllRoleUsers, reloadUserMembers, computed, ref, watch, useCommonMixin, UserModule, useIcons, SVGRenderer, OverviewActionBar, UserTable, KetchUpButton, LoadingSpinner, UsersAndGroupsLoader, eventBus, useI18n, UsersApi }
}

})